<template>
  <v-dialog v-model="dialog" max-width="560" persistent>
    <v-card>
      <v-card-title class="primary white--text py-2">
        <span class="text-subtitle-1 font-weight-bold">Bulk Upload Vendor Contracts</span>
        <v-spacer />
        <v-btn icon dark small @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="form.job_type"
              :items="jobTypeOptions"
              label="Job Type"
              outlined
              dense
              hide-details="auto"
              :error-messages="formErrors.job_type"
              @change="formErrors.job_type = null"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model="form.start_date"
              label="Start Date"
              type="date"
              outlined
              dense
              hide-details="auto"
              :error-messages="formErrors.start_date"
              @input="formErrors.start_date = null"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              v-model="form.end_date"
              label="End Date"
              type="date"
              outlined
              dense
              hide-details="auto"
              :error-messages="formErrors.end_date"
              @input="formErrors.end_date = null"
            />
          </v-col>

          <v-col cols="12">
            <v-file-input
              v-model="file"
              label="Upload Excel File (.xlsx)"
              accept=".xlsx"
              outlined
              dense
              hide-details="auto"
              prepend-icon=""
              prepend-inner-icon="mdi-upload"
              :error-messages="fileError"
              @change="fileError = null"
            />
          </v-col>

          <v-col cols="12" class="d-flex align-center">
            <a href="#" @click.prevent="downloadTemplate" class="text-decoration-none primary--text text-body-2">
              <v-icon small color="primary">mdi-download</v-icon>
              Download Template
            </a>
            <span class="ml-2 text-caption grey--text">
              Columns: Vendor Code, Route, Container Type, Count, Rate
            </span>
          </v-col>

          <v-col v-if="uploadErrors.length" cols="12">
            <v-alert type="error" dense outlined class="text-body-2 mb-0">
              <div v-for="(err, i) in uploadErrors" :key="i">{{ err }}</div>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pb-3 px-4">
        <v-spacer />
        <v-btn depressed @click="close">Cancel</v-btn>
        <v-btn
          depressed
          color="primary"
          :loading="uploading"
          :disabled="!file"
          @click="submit"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "BulkVendorContractUploadDialog",
  props: {
    value: Boolean,
  },
  data() {
    return {
      file: null,
      fileError: null,
      uploading: false,
      uploadErrors: [],
      form: {
        job_type: null,
        start_date: null,
        end_date: null,
      },
      formErrors: {},
      jobTypeOptions: ["Import", "Export", "Domestic"],
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
  },
  methods: {
    close() {
      this.file = null;
      this.fileError = null;
      this.uploadErrors = [];
      this.formErrors = {};
      this.form = { job_type: null, start_date: null, end_date: null };
      this.dialog = false;
    },

    downloadTemplate() {
      const ws = XLSX.utils.aoa_to_sheet([
        ["Vendor Code", "Route", "Container Type", "Count", "Rate"],
        [10000409, "KCT-SCT", "20 Feet", 1, 2100],
        [10000409, "KCT-SCT", "40 Feet", 1, 2100],
        [10001088, "KCT-SCT", "20 Feet", 1, 2100],
        [10001088, "KCT-SIC", "40 Feet", 1, 2000],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Vendor Contracts");
      XLSX.writeFile(wb, "vendor_contracts_template.xlsx");
    },

    validateForm() {
      const errors = {};
      if (!this.form.job_type) errors.job_type = "Job type is required.";
      if (!this.form.start_date) errors.start_date = "Start date is required.";
      if (!this.form.end_date) errors.end_date = "End date is required.";
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    async parseExcel() {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(e.target.result, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
            resolve(rows);
          } catch {
            reject(new Error("Failed to parse Excel file."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsBinaryString(this.file);
      });
    },

    groupRows(rows) {
      const blocks = {};
      for (const row of rows) {
        const vendorCode = String(row["Vendor Code"] ?? "").trim();
        if (!vendorCode) continue;

        if (!blocks[vendorCode]) {
          blocks[vendorCode] = {
            vendor_code: vendorCode,
            job_type: this.form.job_type,
            start_date: this.form.start_date,
            end_date: this.form.end_date,
            rates: [],
          };
        }
        blocks[vendorCode].rates.push({
          route: String(row["Route"] ?? "").trim(),
          container_type: String(row["Container Type"] ?? "").trim(),
          count: Number(row["Count"]),
          rate: Number(row["Rate"]),
        });
      }
      return Object.values(blocks);
    },

    async submit() {
      if (!this.validateForm()) return;
      if (!this.file) {
        this.fileError = "Please select an Excel file.";
        return;
      }

      this.uploadErrors = [];
      this.uploading = true;

      try {
        const rows = await this.parseExcel();
        if (!rows.length) {
          this.fileError = "No data found in the file.";
          this.uploading = false;
          return;
        }
        const data = this.groupRows(rows);

        await this.$api.transporterContract.bulkUploadVendorContracts({ data });
        this.$bus.$emit("showToastMessage", {
          message: `${data.length} vendor contract(s) uploaded successfully!`,
          color: "success",
        });
        this.$emit("refreshList");
        this.close();
      } catch (err) {
        const errData = err?.data;
        if (Array.isArray(errData)) {
          this.uploadErrors = errData.flatMap((block, i) =>
            Object.entries(block).map(([field, msg]) =>
              `Block ${i + 1} — ${field}: ${Array.isArray(msg) ? msg.join(", ") : msg}`
            )
          );
        } else if (errData && typeof errData === "object") {
          this.uploadErrors = Object.entries(errData).map(
            ([field, msg]) => `${field}: ${Array.isArray(msg) ? msg.join(", ") : msg}`
          );
        } else {
          this.uploadErrors = ["Something went wrong. Please check the file and try again."];
        }
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
